/**
 * "Send a quick message" — one round trip, then you land in the thread.
 *
 * `POST /v1/conversations` with a `firstMessage` creates the DM if it does not
 * exist AND sends into it if it does (see routes/conversations.ts), so the card
 * does not need a create-then-send dance and cannot leave an empty DM behind if
 * the second call fails. `clientMsgId` doubles as the idempotency key, so a
 * double-click sends once.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from '@cyphex/ui';
import type { UserId } from '@cyphex/types';
import { ROUTES } from '@/constants/routes';
import { chatApi } from '@/features/chat/api';
import { CHAT_KEYS } from '@/features/chat/constants';

export interface QuickMessageApi {
  readonly isSending: boolean;
  send(userId: UserId, text: string): Promise<boolean>;
}

export function useQuickMessage(): QuickMessageApi {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSending, setSending] = useState(false);

  const send = useCallback(
    async (userId: UserId, text: string): Promise<boolean> => {
      const body = text.trim();
      if (body === '' || isSending) return false;
      setSending(true);
      try {
        const conversation = await chatApi.createConversation({
          kind: 'dm',
          memberIds: [userId],
          firstMessage: { clientMsgId: crypto.randomUUID(), text: body },
        });
        // The chat list is keyed by filter+query, so invalidate the whole
        // `chat` root — a brand-new DM has to appear under every filter it
        // qualifies for, not just the one currently selected.
        await queryClient.invalidateQueries({ queryKey: CHAT_KEYS.all });
        navigate(ROUTES.chatThread(conversation.id));
        return true;
      } catch {
        toast.error('Could not send that message.');
        return false;
      } finally {
        setSending(false);
      }
    },
    [isSending, navigate, queryClient],
  );

  return { isSending, send };
}
